import React from "react";
import { LitChipSet, LitFilterChip, LitInputChip } from "./LitChip";
import { sysColors } from "@/assets/theme/colors";

export type ChipColor = "default" | "strong" | "primary";

interface ChipProps {
  label?: string;
  onClick?(): void;
  onRemove?(e: Event): void;
  selected?: boolean;
  disabled?: boolean;
  softDisabled?: boolean;
  removable?: boolean;
  removeOnly?: boolean;
  showCheckmark?: boolean;
  color?: ChipColor;
  "aria-label"?: string;
}

export const ChipSet = LitChipSet;

function selectedChipColors(containerColor: string, onContainerColor: string): React.CSSProperties {
  return {
    "--md-filter-chip-selected-container-color": containerColor,
    "--md-filter-chip-selected-label-text-color": onContainerColor,
    "--md-filter-chip-selected-leading-icon-color": onContainerColor,
    "--md-filter-chip-selected-hover-label-text-color": onContainerColor,
    "--md-filter-chip-selected-focus-label-text-color": onContainerColor,
    "--md-filter-chip-selected-pressed-label-text-color": onContainerColor,
    "--md-filter-chip-selected-hover-leading-icon-color": onContainerColor,
    "--md-filter-chip-selected-focus-leading-icon-color": onContainerColor,
    "--md-filter-chip-selected-pressed-leading-icon-color": onContainerColor
  } as React.CSSProperties;
}

const colorStyles: Record<ChipColor, React.CSSProperties> = {
  default: {},
  strong: selectedChipColors(sysColors["sys-brand-secondary"], sysColors["sys-brand-on-secondary"]),
  primary: selectedChipColors(sysColors["sys-brand-primary"], sysColors["sys-brand-on-primary"])
};

export default function Chip({
  label,
  showCheckmark = true,
  color = "default",
  removeOnly = false,
  removable,
  onRemove,
  onClick,
  selected,
  disabled,
  softDisabled,
  ...props
}: ChipProps) {
  const chipRef = React.useRef<any>(null);

  React.useEffect(() => {
    if (!showCheckmark && !removeOnly && chipRef.current) {
      // Add CSS to hide the checkmark and fix spacing
      const style = document.createElement("style");
      style.textContent = `
        md-filter-chip[data-hide-checkmark] .leading.icon {
          display: none !important;
        }
        md-filter-chip[data-hide-checkmark] .primary.action {
          padding-inline-start: var(--md-filter-chip-leading-space, 16px) !important;
        }
      `;
      document.head.appendChild(style);

      return () => {
        document.head.removeChild(style);
      };
    }
  }, [showCheckmark, removeOnly]);

  if (removeOnly) {
    return (
      <LitInputChip ref={chipRef} removeOnly selected onRemove={onRemove} {...props}>
        {label}
      </LitInputChip>
    );
  }

  const style = {
    // Apply color styles
    ...colorStyles[color],
    // Hide the checkmark by setting its size to 0 when showCheckmark is false
    ...(showCheckmark === false && {
      "--md-filter-chip-icon-size": "0px",
      "--md-filter-chip-with-leading-icon-leading-space": "var(--md-filter-chip-leading-space, 16px)",
      "--md-filter-chip-icon-label-space": "0px"
    })
  } as React.CSSProperties;

  return (
    <LitFilterChip
      ref={chipRef}
      style={style}
      removable={removable}
      onRemove={onRemove}
      onClick={onClick}
      selected={selected}
      disabled={disabled}
      softDisabled={softDisabled}
      {...(showCheckmark === false && { "data-hide-checkmark": "true" })}
      {...props}
    >
      {label}
    </LitFilterChip>
  );
}
